// Radha — customer credit limits (migration 049). tenant_id/set_by are
// derived server-side by a BEFORE INSERT trigger, never sent from here. No
// native upsert (PostgREST upsert + column-level grants + a stamping trigger
// is more fragile to get right than it's worth) — insert, and on the unique
// violation for an existing customer, fall back to update by name.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

export interface CreditLimit {
  id: string; customer_name: string; credit_limit: string; notes: string | null; updated_at: string;
}

export function useCreditLimits() {
  return useQuery({
    queryKey: ["credit-limits"],
    queryFn: async (): Promise<CreditLimit[]> => {
      const { data, error } = await supabase
        .from("customer_credit_limits")
        .select("id, customer_name, credit_limit, notes, updated_at")
        .order("customer_name", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });
}

export function useSetCreditLimit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ customer_name, credit_limit, notes }: { customer_name: string; credit_limit: number; notes?: string }) => {
      const { error: insErr } = await supabase
        .from("customer_credit_limits")
        .insert({ customer_name, credit_limit, notes: notes ?? null });
      if (!insErr) return;
      if (insErr.code !== "23505") throw insErr; // anything but "already exists" is a real failure
      const { error: updErr } = await supabase
        .from("customer_credit_limits")
        .update({ credit_limit, notes: notes ?? null })
        .eq("customer_name", customer_name);
      if (updErr) throw updErr;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["credit-limits"] }),
  });
}

export function creditLimitErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : (err && typeof err === "object" && "message" in err ? String((err as { message: unknown }).message) : String(err));
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
